import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Stack, Text } from '@solvhealth/jigsaw-v1';
import { useCookies } from 'react-cookie';
import { hideModal, showModal } from '~/actions/runtime';
import Hidden from '../SolvDesignSystem/Hidden';
import CenteredDialog from '../SolvDesignSystem/Dialog/CenteredDialog';
import SlideInBottomDialog from '../SolvDesignSystem/Dialog/SlideInBottomDialog';
import { isModalOpen } from '~/components/ModalController/util/modal';
import useSelectFetchMostRecentBookingLocationDetails from '~/components/AccountGetsMarried/hooks/useSelectFetchMostRecentBookingLocationDetails';
import { formatLocationName } from '~/core/util/location';
import useHasFluShot from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/BookFluShot/hooks/useHasFluShot';
import { useSolvSelector } from '~/reducers';
import { analyticsTrackEvent } from '~/core/analytics';
import { FluShotDriverModalEvents } from '~/core/analytics/events';
import { trackingProperties } from '~/components/Account/Profile/Insurance/util/insuranceDetails';
import {
  shouldRouteToPrioritizedLocation,
  useHandleRouteCDPorUniversalSearch,
} from '~/hooks/useHandleRouteCDPorUniversalSearch';
import { FLU_SHOT_SERVICE_ID } from '~/constants/services';
import { MEMBERSHIP_SUCCESS_DIALOG } from '../Membership/Signup/SuccessDialog';

const StyledImg = styled.img.withConfig({ componentId: 'sc-1nda4rb-0' })`
  margin-bottom: 15px;
`;

export const FLU_SHOT_MODAL_FEATURE_FLAG = false;
export const FLU_SHOT_MODAL_ID = 'modal/fluShot';
export const FLU_SHOT_DRIVER_DISMISSED_COOKIE = 'flu shot driver dismissed cookie';
const MONTH_IN_MILLISECONDS = 2629800000;

type Props = {
  userProfileId: any;
};
export const FluShotModal = ({ userProfileId }: Props) => {
  const runtime = useSolvSelector((state: any) => state.runtime);
  const [, setCookies] = useCookies();
  const dispatch = useDispatch();
  const { isLoading, location: prioritizedLocation } =
    useSelectFetchMostRecentBookingLocationDetails(userProfileId);
  const prioritizedLocationOffersFluShot = useHasFluShot(prioritizedLocation);
  const willRouteToPhysicalLocation = shouldRouteToPrioritizedLocation(
    prioritizedLocation,
    prioritizedLocationOffersFluShot
  );

  useEffect(() => {
    dispatch(showModal(FLU_SHOT_MODAL_ID));
  }, [dispatch]);

  const open =
    isModalOpen(runtime.modal, FLU_SHOT_MODAL_ID) &&
    !isLoading && // Wait until prioritizedLocation info is loaded to open the modal
    !isModalOpen(runtime.modal, MEMBERSHIP_SUCCESS_DIALOG); // These two modals are both on the account home. Don't let them overlap.

  useEffect(() => {
    if (open) {
      analyticsTrackEvent(FluShotDriverModalEvents.LOADED, trackingProperties);
    }
  }, [open]);

  const setSeenCookie = () => {
    setCookies(FLU_SHOT_DRIVER_DISMISSED_COOKIE, 'dismissed', {
      path: '/',
      expires: new Date(+new Date() + MONTH_IN_MILLISECONDS),
    });
  };

  const routeToFluShot = useHandleRouteCDPorUniversalSearch(
    () => {},
    prioritizedLocation,
    prioritizedLocationOffersFluShot,
    [FLU_SHOT_SERVICE_ID]
  );

  const handleDismiss = () => {
    analyticsTrackEvent(FluShotDriverModalEvents.DISMISS_CLICKED, trackingProperties);
    setSeenCookie();
    dispatch(hideModal(FLU_SHOT_MODAL_ID));
  };

  const handleBookNow = () => {
    analyticsTrackEvent(FluShotDriverModalEvents.BOOK_NOW_CLICKED, trackingProperties);
    setSeenCookie();
    routeToFluShot();
  };

  const locationNameToBookAt = willRouteToPhysicalLocation
    ? formatLocationName(prioritizedLocation)
    : '';

  const content = (
    <>
      <Stack alignItems="center" margin="auto" maxWidth={450} paddingBottom={5} space={1}>
        <StyledImg alt="Get your flu shot" height="110" src="/images/account/Arm-Bandaid.png" />
        <Text variant="h2">Book a flu shot today</Text>
        <Text muted paddingBottom={2} textAlign="center" variant="body">
          {locationNameToBookAt
            ? `Save time and book an appointment at ${locationNameToBookAt} to skip the wait.`
            : 'Save time and book an appointment to skip the wait.'}
        </Text>
        <Button color="primary" fullWidth onClick={handleBookNow} variant="contained">
          Book now
        </Button>
        <Button color="primary" fullWidth onClick={handleDismiss} variant="text">
          No, thanks
        </Button>
      </Stack>
    </>
  );

  return (
    <>
      <Hidden only={['xs']}>
        <CenteredDialog onClose={handleDismiss} open={open}>
          {content}
        </CenteredDialog>
      </Hidden>
      <Hidden only={['sm', 'md', 'lg', 'xl']}>
        <SlideInBottomDialog onClose={handleDismiss} open={open} shouldOffsetBottomNav={false}>
          {content}
        </SlideInBottomDialog>
      </Hidden>
    </>
  );
};
